import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Command, CommandCheck, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { cn } from './utils/shadcn-utils';
const asyncSelectTriggerVariants = cva("border-input-border data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-expanded:border-ring aria-expanded:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[var(--focus-ring-width)] aria-expanded:ring-[var(--focus-ring-width)] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", {
    variants: {
        variant: {
            default: 'bg-input dark:bg-input/30 dark:hover:bg-input/50',
            muted: 'bg-muted border-0 focus-within:ring-ring'
        },
        selectSize: {
            default: 'min-h-9',
            sm: 'min-h-7',
            lg: 'min-h-10'
        }
    },
    defaultVariants: {
        variant: 'default',
        selectSize: 'default'
    }
});
export function AsyncSelect(props) {
    const { value, onChange, onBlur, placeholder, options, isLoading, onSearchValueChange, onEndReached, onOpenChange, disabled, className, getOptionId, renderOption, renderValue, variant, selectSize, multi } = props;
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const listRef = React.useRef(null);
    const isMulti = multi === true;
    const emitSearchValueChange = useDebouncedCallback((text) => {
        if (onSearchValueChange) {
            onSearchValueChange(text);
        }
    }, 300);
    const handleListScroll = React.useCallback(() => {
        if (!onEndReached || !open) {
            return;
        }
        const el = listRef.current;
        if (!el) {
            return;
        }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= 24) {
            onEndReached();
        }
    }, [onEndReached, open]);
    const executeGetOptionId = React.useCallback((option) => {
        return option ? getOptionId(option) : '';
    }, [getOptionId]);
    const selectedId = React.useMemo(() => {
        if (isMulti) {
            return '';
        }
        return value && !Array.isArray(value) ? executeGetOptionId(value) : '';
    }, [isMulti, value, executeGetOptionId]);
    const selectedIdSet = React.useMemo(() => {
        if (!isMulti) {
            return new Set();
        }
        const arr = Array.isArray(value) ? value : [];
        return new Set(arr.map(executeGetOptionId));
    }, [isMulti, value, executeGetOptionId]);
    const multiValuesNodes = React.useMemo(() => {
        if (!isMulti) {
            return null;
        }
        const arr = Array.isArray(value) ? value : [];
        if (arr.length === 0) {
            return null;
        }
        return arr.map((v, idx) => {
            const id = executeGetOptionId(v);
            return (_jsxs("div", { className: "px-1 rounded-md bg-muted flex items-center gap-1", children: [renderValue ? renderValue(v) : id, _jsx("button", { type: "button", className: "inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/10", onClick: e => {
                            e.preventDefault();
                            e.stopPropagation();
                            const currentArray = Array.isArray(value) ? value : [];
                            const existingIndex = currentArray.findIndex(o => executeGetOptionId(o) === id);
                            let nextArray;
                            if (existingIndex >= 0) {
                                nextArray = [...currentArray.slice(0, existingIndex), ...currentArray.slice(existingIndex + 1)];
                            }
                            else {
                                nextArray = currentArray;
                            }
                            onChange(nextArray);
                        }, "aria-label": "Remove", children: _jsx(XIcon, { className: "size-3" }) })] }, id));
        });
    }, [isMulti, value, renderValue, executeGetOptionId]);
    const selectedCount = React.useMemo(() => {
        if (isMulti) {
            return Array.isArray(value) ? value.length : 0;
        }
        return selectedId ? 1 : 0;
    }, [isMulti, value, selectedId]);
    const handleOpenChange = React.useCallback((next) => {
        if (disabled) {
            return;
        }
        setOpen(next);
        onOpenChange?.(next);
    }, [disabled, onOpenChange]);
    const normalizedOptions = React.useMemo(() => options ?? [], [options]);
    return (_jsxs(Popover, { open: open, modal: true, onOpenChange: handleOpenChange, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", role: "combobox", "aria-expanded": open, className: cn(asyncSelectTriggerVariants({ variant, selectSize }), 'w-full justify-between text-left', className), disabled: disabled, onBlur: () => {
                        if (onBlur) {
                            onBlur();
                        }
                    }, children: [_jsx("span", { className: cn('truncate flex flex-row gap-2 flex-wrap', selectedCount === 0 && 'text-muted-foreground'), children: selectedCount > 0 ? (isMulti ? multiValuesNodes : renderValue ? renderValue(value) : selectedId) : (placeholder ?? '') }), _jsx(ChevronDownIcon, { className: "size-4" })] }) }), _jsx(PopoverContent, { className: "w-[var(--radix-popper-anchor-width)] p-0", children: _jsxs(Command, { shouldFilter: false, children: [_jsx(CommandInput, { placeholder: 'Vyhledávání...', value: searchValue, autoFocus: true, onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    setOpen(false);
                                }
                                if (e.key === 'Escape') {
                                    setOpen(false);
                                }
                            }, onValueChange: next => {
                                setSearchValue(next);
                                emitSearchValueChange(next);
                            } }), _jsx(CommandList, { ref: listRef, onScroll: handleListScroll, children: isLoading === true && normalizedOptions.length === 0 ? (_jsx(CommandEmpty, { className: "py-4 text-center", children: "Na\u010D\u00EDt\u00E1n\u00ED..." })) : normalizedOptions.length === 0 ? (_jsx(CommandEmpty, { className: "py-4 text-center", children: "\u017D\u00E1dn\u00E9 polo\u017Eky" })) : (_jsxs(CommandGroup, { children: [normalizedOptions.map(opt => {
                                        const id = executeGetOptionId(opt);
                                        const isSelected = isMulti ? selectedIdSet.has(id) : selectedId === id;
                                        return (_jsxs(CommandItem, { value: id, onSelect: currentValue => {
                                                if (!isMulti) {
                                                    if (currentValue === selectedId) {
                                                        setOpen(false);
                                                        return;
                                                    }
                                                    const next = options.find(o => executeGetOptionId(o) === currentValue) ?? null;
                                                    onChange(next);
                                                    setOpen(false);
                                                    return;
                                                }
                                                const nextOption = options.find(o => executeGetOptionId(o) === currentValue);
                                                if (!nextOption) {
                                                    return;
                                                }
                                                const currentArray = Array.isArray(value) ? value : [];
                                                const existingIndex = currentArray.findIndex(o => executeGetOptionId(o) === currentValue);
                                                let nextArray;
                                                if (existingIndex >= 0) {
                                                    nextArray = [...currentArray.slice(0, existingIndex), ...currentArray.slice(existingIndex + 1)];
                                                }
                                                else {
                                                    nextArray = [...currentArray, nextOption];
                                                }
                                                onChange(nextArray);
                                            }, children: [_jsx("span", { className: "truncate", children: renderOption ? renderOption(opt, { isSelected }) : id }), isSelected ? _jsx(CommandCheck, {}) : null] }, id));
                                    }), isLoading ? _jsx("div", { className: "py-2 text-center text-muted-foreground text-sm", children: "Na\u010D\u00EDt\u00E1n\u00ED..." }) : null] })) })] }) })] }));
}
//# sourceMappingURL=AsyncSelect.js.map